// Categorías de documentos (financiero.*_categorias vía RPC) — Financiero → Configuración.
// Sirven para clasificar comprobantes recibidos/emitidos en Procesar Documentos.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, Switch, fieldCls } from '@/features/inventario/shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

type Tipo = 'recibido' | 'emitido';

interface Categoria {
  id: string;
  tipo: Tipo;
  nombre: string;
  orden: number;
  es_activo: boolean;
}

const vacio = { nombre: '', orden: 0, es_activo: true };

const TABS: { tipo: Tipo; label: string }[] = [
  { tipo: 'recibido', label: 'Recibidos' },
  { tipo: 'emitido', label: 'Emitidos' },
];

export function CategoriasDocumento() {
  const qc = useQueryClient();
  const { data: rows = [], isFetching } = useQuery<Categoria[]>({
    queryKey: ['fin-categorias'],
    queryFn: async () => (await apiClient.post('/rpc/fin_categorias', {}, FIN)).data || [],
  });

  const [tipo, setTipo] = useState<Tipo>('recibido');
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const filas = useMemo(
    () => rows
      .filter((r) => r.tipo === tipo)
      .slice()
      .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, 'es', { numeric: true })),
    [rows, tipo],
  );

  const abrir = (c?: Categoria) => {
    if (c) setForm({ nombre: c.nombre, orden: c.orden, es_activo: c.es_activo });
    else setForm(vacio);
    setEditId(c?.id ?? null);
    setAbierto(true);
  };

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['fin-categorias'] });

  const guardar = useMutation({
    mutationFn: () => apiClient.post('/rpc/fin_categoria_guardar', {
      _id: editId,
      _tipo: tipo,
      _nombre: form.nombre.trim(),
      _orden: Number(form.orden) || 0,
      _es_activo: form.es_activo,
    }, FIN),
    onSuccess: () => { toast.success('Categoría guardada'); setAbierto(false); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/fin_categoria_eliminar', { _id: id }, FIN),
    onSuccess: () => { toast.success('Categoría eliminada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const dlg = useDialogoEnLinea();

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tags className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-semibold">Categorías de Documentos</h1>
        </div>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva categoría</Button>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-3 text-sm text-muted-foreground">
          Estas categorías son las que aparecen al clasificar en <strong>Procesar Recibidos/Emitidos</strong>. Son propias de cada empresa.
        </CardContent>
      </Card>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.tipo}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tipo === t.tipo ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTipo(t.tipo)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{TABS.find((t) => t.tipo === tipo)?.label} ({filas.length})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th>
                <th className="py-2 pr-3">Nombre</th>
                <th className="py-2 pr-3">Orden</th>
                <th className="py-2 pr-3">Activa</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin categorías de {TABS.find((t) => t.tipo === tipo)?.label.toLowerCase()}</td></tr>
              )}
              {filas.map((c, i) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{c.nombre}</td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{c.orden}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activa' : 'Inactiva'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button
                      className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1"
                      title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar la categoría "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar categoría' : `Nueva categoría (${TABS.find((t) => t.tipo === tipo)?.label})`} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Nombre *">
              <input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} autoFocus />
            </Campo>
            <Campo label="Orden">
              <input
                type="number"
                className={fieldCls}
                value={form.orden}
                onChange={(e) => { const n = Number(e.target.value); setForm({ ...form, orden: Number.isFinite(n) ? n : 0 }); }}
              />
            </Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activa" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
